import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Modal } from 'antd';

import { ToastNotifications } from 'src/core/ui/toast';
import { ControllerProps } from 'src/router';
import { DialogService } from 'src/core/services/ui/dialog.service';
import { ObjectUtils } from 'src/core/utils/object';
import { ContextService } from 'src/core/services/context.service';
import { ClientFormView } from 'src/app/crm/clients/form/client-form.view';
import { ClientDataService } from 'src/app/crm/clients/form/client-form.data';
import { EntitySchemaDataService } from 'src/app/general/entity-schemas/entity-schema.data';
import { IdentityService } from 'src/core/services/identity.service';
import { FormatService } from 'src/core/services/format.service';

interface PaxClientProps extends ComponentViewProps<QuotePaxItem> {
	type: string;
	billing: BillingItem[];
	closeDialog: (ev?: React.MouseEvent<HTMLButtonElement>) => void;
	formatService: FormatService;
	showModal: boolean;
	defaultLanguage: string;
}

@observer
export class PaxClientFormController extends React.Component<PaxClientProps, any> {
	@observable
	protected dataService: ClientDataService;

	pax: QuotePaxItem;

	billing: BillingItem[];

	@resolve
	protected dialogService: DialogService;

	@resolve
	protected identityService: IdentityService;

	@resolve
	protected contextService: ContextService;

	@resolve
	protected toast: ToastNotifications;

	constructor(props) {
		super(props);
		this.state = { mode:'edit' , schema: undefined }
	}

	@computed
	protected get title() {
		if (this.dataService && this.dataService.item) {
			if (this.dataService.item.type == 0) {
				return `Client ${this.dataService.item.dynamicProperties['person']['firstName'] || ''} ${this.dataService.item.dynamicProperties['person']['lastName'] || ''}`;
			} else {
				return `Client ${this.dataService.item.dynamicProperties['company']['signature'] || ''}`;
			}
		}
		return '';
	}

	@autobind
	protected onModeChanged(mode: ControlMode) {

		this.setState({ mode: mode });
	}


	@autobind
	convertToClient(pax: QuotePaxItem) {
		this.dataService.items = [];
		this.props.setBusy(true);
		this.dataService.add('Person', this.identityService.userId,this.props.defaultLanguage)
			.then(res => {
				let model: PersonItem = { title: pax.dynamicProperties['salutation'], firstName: pax.dynamicProperties['foreName'], middleName: pax.dynamicProperties['middleName'], lastName: pax.dynamicProperties['surName'], birthDate: new Date(pax.dynamicProperties['birthDate']), gender: pax.dynamicProperties['gender'], salutation: pax.dynamicProperties['salutation'] };
				res.dynamicProperties['person'] = model;
				res.displayName = model.firstName.concat(' '.concat(model.middleName.concat(' '.concat(model.lastName))));
				pax.clientId = res.id;
				pax.clientDisplayName = res.displayName;
				if (this.billing.find(x => x.id == res.id) == null)
					this.billing.push({ id: res.id, displayName: res.displayName });
				this.props.setBusy(false);
			}).catch(error => {
				this.props.setBusy(false);
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			});
	}

	@autobind
	public async save() {
		const item = this.dataService.item;
		const errors = await item.validate(true);
		if (errors.length == 0) {
			this.props.setBusy(true);
			if (item.type == 0) {
				item.displayName = `${item.dynamicProperties.person.firstName} ${item.dynamicProperties.person.lastName}`;
			} else {
				item.displayName = `${item.dynamicProperties.company.signature}`;
			}
			this.dataService.save()
				.then(res => {
					const item = this.dataService.items[0];
					let pax = this.props.model;
					let model = item.dynamicProperties.person;
					this.props.setBusy(false);
					//this.onModeChanged('display');
					pax.clientId = item.entity.id;
					pax.clientDisplayName = this.dataService.item.displayName;
					pax.dynamicProperties['salutation'] = model.salutation;
					pax.dynamicProperties['foreName'] = model.firstName;
					pax.dynamicProperties['middleName'] = model.middleName;
					pax.dynamicProperties['surName'] = model.lastName;
					pax.dynamicProperties['birthDate'] = new Date(model.birthDate);
					pax.dynamicProperties['gender'] = model.gender;
					this.toast.showDataSaved();
					this.props.closeDialog();
				})
				.catch((error) => {
					this.props.setBusy(false);
					//this.onModeChanged('display');

					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
					this.props.closeDialog();
				});
		} else {
			const err = errors.map(e => e.text);
			this.toast.showToast('Validation error!', err, 'error');
		}
	}

	@autobind
	public cancel() {
		return new Promise<boolean>((resolve, reject) => {
			if (this.dataService.isDirty) {
				this.dialogService.showYesNo('Warning', 'Are you sure want to close this form? Your changes will be lost!')
					.then(dialogResult => {
						if (dialogResult) {
							if (this.dataService.item.id == null || this.dataService.item.id<0) {
								let index = this.billing.findIndex(x => x.id == this.props.model.clientId);
								if (index != -1) {
									this.billing.splice(index, 1);
								}
								this.props.model.clientId = undefined;
								this.props.model.clientDisplayName = undefined;
							}
							this.props.closeDialog();
						}
						resolve(true);
					})
					.catch(reject);
			} else {
				this.props.closeDialog();
				resolve(true);
			}
		});
	}

	public componentDidMount() {
		this.fetchSchema()
			.then(() => {
				this.dataService = ClientDataService.make(this.contextService);
				this.pax = this.props.model;
				this.billing = this.props.billing;
				if (this.pax.clientId == null)
					this.convertToClient(this.pax);
				else {
					this.dataService.addFilter('id', 'eq', this.pax.clientId);
					this.dataService.addSorting('id', 'asc', true);
					this.dataService.pageSize = 1;
					this.dataService.load(query => query);
				}
			});
	}

	componentWillReceiveProps(nextProps: Readonly<PaxClientProps>, nextContext: any) {
		// this.dataService = ClientDataService.make(this.contextService);
		if (nextProps.model != this.props.model) {
			this.pax = nextProps.model;
			this.billing = nextProps.billing;
			if (this.pax.clientId == null) {
				this.onModeChanged('edit');
				this.convertToClient(this.pax);
			}
			else {
				this.onModeChanged('edit');
				this.dataService.addFilter('id', 'eq', this.pax.clientId);
				this.dataService.addSorting('id', 'asc', true);
				this.dataService.pageSize = 1;
				this.dataService.load(query => query);
			}

		}

	}

	fetchSchema(): Promise<void> {
		const entitySchemaService = EntitySchemaDataService.make();
		entitySchemaService.pageSize = 1;

		entitySchemaService.addFilter('name', 'eq', 'client');
		entitySchemaService.addSorting('name', 'asc', true);
		return entitySchemaService.load(query => query)
			.then((data) => {
				const entitySchema = data[0];
				const schema = ObjectUtils.toObject<JsonSchema>(entitySchema.schema);
				this.setState({ schema: schema });
			});
	}

    render() {
        if (this.dataService == null || this.dataService.item == null) {
            return null;
        }
		if (this.dataService.item.dynamicProperties['notes'] == null) {
			this.dataService.item.dynamicProperties['notes'] = [];
		}
		return(<Modal className="ant-modalClient"
			visible={this.props.showModal}
			onCancel={this.cancel}
			title={this.title}
			maskClosable={false}
			footer={[
				<Button key='save' onClick={this.state.mode == "edit" ? this.save : this.props.closeDialog}>{this.state.mode == "edit" ? 'Save' : "Ok"}</Button>,
				<Button key='cancel' onClick={this.cancel}>Cancel</Button>
			]}>
			<ClientFormView
				title={this.title}
				identityService={this.identityService}
				mode={this.state.mode}
				onModeChange={this.onModeChanged}
				cancel={this.cancel}
				save={this.save}
				model={this.dataService.item}
				contextService={this.contextService}
				setBusy={this.props.setBusy}
				schema={this.state.schema}
				isNew={true}
				offset={false}
				showHeader={false}
				className={false}
			/>
		</Modal>)       
        ;
    }
}



// WEBPACK FOOTER //
// ./src/app/bookings/quote-paxs/pax-client-form.controller.tsx